/**
 * Vendor shipping-label barcodes.
 *
 * Quaker is the only one of the three non-Marvin vendors whose barcode
 * identifies what is inside the carton. Its unit barcode concatenates the
 * sales order, the order line and the unit index, so a scan resolves to an
 * exact line and an exact one of that line's units with no matching, no
 * scoring and no ambiguity -- the same standing Marvin's barcodes have.
 *
 * Kolbe (`3564417 1of1`) and Windsor (`1303000189`) print opaque carton
 * serials instead. Those identify a box, not its contents, so they stay with
 * the weighted matcher and are deliberately NOT parsed here: returning a
 * confident-looking result for a code that cannot support one is worse than
 * returning nothing.
 *
 * Verified against the label photos in
 * fwwindsorquakerandkolbelabelphotosforscanning.zip, where each barcode sits
 * beside its own printed SO / LINE / UNIT fields:
 *
 *   D530070040001  ->  SO D53007, LINE 004, UNIT 0001 of 0004
 *   D149250020010  ->  SO D14925, LINE 002, UNIT 0010 of 0015
 *   D404620040001  ->  SO D40462, LINE 004, UNIT 0001 of 0002
 *
 * A unit too big for one carton -- a sliding door and its panels, a
 * combination unit shipped in pieces -- ships as several PACKAGES, and each
 * package's barcode carries a "P" and a package number. Quaker's delivery
 * ticket lists them as "SO#_Line#_Package_Completion" (9/11/26 ticket for
 * D54524: SD54524_L001_P001_C0001 .. _P003_C0001 for one door), and the dock
 * scanned the matching barcode off 01800038A's combination unit on 9/28/26:
 *
 *   PD454330020010001  ->  SO D45433, LINE 002, PACKAGE 001, UNIT 0001
 *
 * The same ticket names accessory cartons "SO#_Package" (AD54524_P004).
 *
 * An accessory carton's own barcode -- the screens, the mull kit, anything
 * that is not one of a line's units -- is that name with the underscore and
 * the "P" taken out: "A" + SO + PACKAGE(3). It was the one form the dock had
 * not met until 10/5/26, when a screen carton came up "Unknown barcode" (the
 * phone's camera had read retail UPC-E digits off its bars). The package
 * sticker for SO D60748, PO 03700030A, PKG 001 prints SHP AD60748_P001 and,
 * above its Code 128 bars, the digits "AD60748001". Every Quaker sticker puts
 * its barcode's own text there -- the unit stickers' "D530070040001" is what
 * decodes -- so the form is read from that line:
 *
 *   AD60748001  ->  SO D60748, PACKAGE 001, no line and no unit
 *
 * (The photo is too coarse to decode the bars themselves; the first real scan
 * of one is the confirmation.) An accessory names no line -- the sticker's
 * "Lin 005, Parent 001" is not in the barcode -- and no unit, so it is never
 * credited to an item, and quakerUnitKey is null for it.
 *
 * The form is read only for sales orders starting "D", the one letter every
 * Quaker order seen so far has (D60748, D53007, D54524, D45433 ...). An
 * accessory carton's barcode and the SHP field printed beside it name the same
 * carton, and quakerCartonKey is what says so.
 */

/** Line (3) and unit (4) are fixed width; everything before them is the SO. */
const LINE_DIGITS = 3;
const UNIT_DIGITS = 4;
/** A package number (3) sits between the line and the unit. */
const PACKAGE_DIGITS = 3;

/**
 * e.g. "SD53007_L004_C0001", or "SD54524_L001_P002_C0001" for one package of
 * a unit -- the label's printed SHP field.
 */
const SHIPMENT = /^S([A-Z]\d+)_L(\d{3})(?:_P(\d{3}))?_C(\d{4})$/;

/** e.g. "AD54524_P004" -- an accessory carton, as the delivery ticket prints it. */
const ACCESSORY = /^A([A-Z]\d+)_P(\d{3})$/;

/**
 * e.g. "AD60748001" -- the same carton's barcode: "A", the sales order's
 * letter and digits, then the three-digit package. Two letters then digits
 * is a shape no unit code has (those have one), and the leading "A" is what
 * keeps the other vendors' codes out: Marvin's tracking numbers start U or V,
 * its carton numbers E, and Kolbe's and Windsor's serials are all digits.
 *
 * Unlike the other forms this one is not read for any sales-order letter, only
 * "D": it is the one shape here that was not read off a decoded barcode, and
 * a scan that parses is looked up as a Quaker order BEFORE the packing-slip
 * import is tried, so a looser one (any "A", any letter, six digits) would
 * take such a code from whoever it really belongs to. The cost of being too
 * strict is the old one -- the carton comes up unknown, and its printed
 * AD60748_P001 still reads -- so a Quaker order that starts with another
 * letter is one line to add here, once seen.
 */
const ACCESSORY_BARCODE = /^A(D)(\d+)$/;

/**
 * One letter, then digits. The letter is required, and is what keeps
 * Windsor's and Kolbe's all-numeric serials from being read as Quaker codes.
 * Every Quaker sales order seen so far starts "D"; should an all-numeric one
 * ever turn up, this parser will decline it rather than guess, which is the
 * intended failure.
 */
const CODE = /^([A-Z])(\d+)$/;

/** "P", the sales order's letter, then digits: one package of a unit. */
const PACKAGE = /^P([A-Z])(\d+)$/;

/** Smallest sales-order number we will believe, in digits. */
const MIN_ORDER_DIGITS = 3;

/**
 * Parse a Quaker shipping-label barcode.
 *
 * Values are returned exactly as printed, as strings. `lineNumber` keeps its
 * leading zeros ("004", not 4) because the printed label, the SHP field and
 * the barcode all agree on that form, and because coercing vendor line
 * numbers to Number has bitten this codebase before -- Marvin's "10a"
 * accessory lines became 0. Callers that need to compare against a sale
 * item's numeric line should convert at the point of comparison, where the
 * intent is visible.
 *
 * `package` is set only for a carton that is one of several making up a
 * unit (or an accessory carton): the unit is the thing ordered, and two of
 * its packages are still one unit.
 *
 * @param {string} raw - Scanned code. Whitespace and case are tolerated.
 * @returns {?{
 *   vendor: "Quaker",
 *   kind: "unit"|"package"|"shipment"|"accessory"|"order",
 *   salesOrder: string,
 *   lineNumber: ?string,
 *   package: ?string,
 *   unit: ?string,
 *   raw: string,
 * }} null when the code is not a Quaker barcode. Never a partial guess.
 */
export function parseQuakerBarcode(raw) {
  if (typeof raw !== "string" && typeof raw !== "number") return null;
  const original = String(raw);
  const code = original.trim().toUpperCase().replace(/\s+/g, "");
  if (!code) return null;

  const parsed = (kind, salesOrder, lineNumber, packageNumber, unit) => ({
    vendor: "Quaker",
    kind,
    salesOrder,
    lineNumber: lineNumber || null,
    package: packageNumber || null,
    unit: unit || null,
    raw: original,
  });

  const shipment = SHIPMENT.exec(code);
  if (shipment) {
    const [, salesOrder, lineNumber, packageNumber, unit] = shipment;
    return parsed("shipment", salesOrder, lineNumber, packageNumber, unit);
  }

  const accessory = ACCESSORY.exec(code);
  if (accessory) {
    return parsed("accessory", accessory[1], null, accessory[2], null);
  }

  // The barcode form. Like a package code, "A" + a letter is never a unit
  // code, so one that does not fit -- no room for an order and a package --
  // is refused outright rather than re-read as an order or a unit.
  const accessoryCode = ACCESSORY_BARCODE.exec(code);
  if (accessoryCode) {
    const [, letter, digits] = accessoryCode;
    if (digits.length < MIN_ORDER_DIGITS + PACKAGE_DIGITS) return null;
    const split = digits.length - PACKAGE_DIGITS;
    return parsed(
      "accessory",
      letter + digits.slice(0, split),
      null,
      digits.slice(split),
      null
    );
  }

  // A package code is "P" plus a whole unit code with a package number
  // spliced in. "P" followed by a letter is never a unit code (those have one
  // letter), so a code of that shape which does not fit is refused outright
  // rather than re-read as something else.
  const packageCode = PACKAGE.exec(code);
  if (packageCode) {
    const [, letter, digits] = packageCode;
    const tail = LINE_DIGITS + PACKAGE_DIGITS + UNIT_DIGITS;
    if (digits.length < MIN_ORDER_DIGITS + tail) return null;
    const split = digits.length - tail;
    return parsed(
      "package",
      letter + digits.slice(0, split),
      digits.slice(split, split + LINE_DIGITS),
      digits.slice(split + LINE_DIGITS, split + LINE_DIGITS + PACKAGE_DIGITS),
      digits.slice(split + LINE_DIGITS + PACKAGE_DIGITS)
    );
  }

  const match = CODE.exec(code);
  if (!match) return null;
  const [, letter, digits] = match;

  // Short enough to be the order-only barcode (the second, smaller barcode on
  // the label) rather than a truncated unit code.
  if (digits.length < MIN_ORDER_DIGITS + LINE_DIGITS + UNIT_DIGITS) {
    return digits.length >= MIN_ORDER_DIGITS
      ? parsed("order", letter + digits)
      : null;
  }

  const split = digits.length - LINE_DIGITS - UNIT_DIGITS;
  return parsed(
    "unit",
    letter + digits.slice(0, split),
    digits.slice(split, split + LINE_DIGITS),
    null,
    digits.slice(split + LINE_DIGITS)
  );
}

/**
 * The unit a Quaker carton belongs to, as one key: every package of a unit
 * shares it, and so do the unit's barcode and its printed SHP field. Null for
 * anything that does not name a unit (an order or accessory code, or a code
 * that is not Quaker's).
 */
export function quakerUnitKey(raw) {
  const parsed = parseQuakerBarcode(raw);
  if (!parsed || !parsed.lineNumber || !parsed.unit) return null;
  return [parsed.salesOrder, parsed.lineNumber, parsed.unit].join("|");
}

/**
 * The carton a Quaker code names, as one key: a code and the SHP field printed
 * beside it name the same carton (D530070040001 and SD53007_L004_C0001;
 * AD60748001 and AD60748_P001), so receiving the one and then typing the
 * other is a second look at the same box, not a second box. Two packages of
 * one unit are two cartons, so the package is part of the key; an accessory's
 * has no line or unit, which is what keeps it from ever equalling a unit's.
 * Null for anything that names no carton (the order-only code, or a code that
 * is not Quaker's).
 */
export function quakerCartonKey(raw) {
  const parsed = parseQuakerBarcode(raw);
  if (!parsed || parsed.kind === "order") return null;
  return [
    parsed.salesOrder,
    parsed.lineNumber || "",
    parsed.package || "",
    parsed.unit || "",
  ].join("|");
}

/**
 * Whether an order's vendor is Quaker. Matched by prefix because the same
 * vendor arrives under several names: "Quaker Residential" and "Quaker
 * Commercial" are both Quaker.
 */
export function isQuakerVendor(vendor) {
  return String(vendor || "")
    .trim()
    .toLowerCase()
    .startsWith("quaker");
}
